import React from 'react';
import styled from 'styled-components';
import { Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaUsers, FaCircle } from 'react-icons/fa';
import { useI18n } from '../../i18n';

const CollaboratorContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CollaboratorAvatars = styled.div`
  display: flex;
  align-items: center;
`;

const Avatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: ${props => props.$color || '#6c757d'};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  border: 2px solid white;
  margin-left: ${props => props.$index > 0 ? '-8px' : '0'};
  cursor: pointer;
  position: relative;
  z-index: ${props => 10 - props.$index};

  &:hover {
    z-index: 20;
    transform: scale(1.1);
    transition: transform 0.1s ease;
  }
`;

const OnlineIndicator = styled(FaCircle)`
  color: #28a745;
  font-size: 8px;
  position: absolute;
  bottom: 0;
  right: 0;
`;

const CollaboratorCount = styled(Badge)`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  font-size: 12px;
`;

const CollaboratorList = ({
  collaborators = [],
  currentUserId,
  maxDisplay = 4,
  showCount = true,
  compact = false
}) => {
  const { t } = useI18n();

  // Get initials from display name
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Filter out current user for display (they know they're there)
  const otherCollaborators = collaborators.filter(c => c.user_id !== currentUserId);
  const displayCollaborators = otherCollaborators.slice(0, maxDisplay);
  const remainingCount = otherCollaborators.length - displayCollaborators.length;
  const totalCount = collaborators.length;

  if (totalCount === 0) {
    return null;
  }

  // In compact mode, just show count
  if (compact) {
    return (
      <CollaboratorCount bg="info">
        <FaUsers />
        {totalCount}
      </CollaboratorCount>
    );
  }

  return (
    <CollaboratorContainer>
      {showCount && totalCount > 1 && (
        <CollaboratorCount bg="info">
          <FaUsers />
          {t('spreadsheet:collaboration.online', { count: totalCount })}
        </CollaboratorCount>
      )}

      <CollaboratorAvatars>
        {displayCollaborators.map((collaborator, index) => (
          <OverlayTrigger
            key={collaborator.user_id}
            placement="bottom"
            overlay={
              <Tooltip>
                {collaborator.display_name || collaborator.username}
                {collaborator.cursor_position && (
                  <span className="ms-1 text-muted">
                    ({t('spreadsheet:collaboration.editing')})
                  </span>
                )}
              </Tooltip>
            }
          >
            <Avatar
              $color={collaborator.color}
              $index={index}
            >
              {getInitials(collaborator.display_name || collaborator.username)}
              <OnlineIndicator />
            </Avatar>
          </OverlayTrigger>
        ))}

        {remainingCount > 0 && (
          <OverlayTrigger
            placement="bottom"
            overlay={
              <Tooltip>
                {otherCollaborators.slice(maxDisplay).map(c =>
                  c.display_name || c.username
                ).join(', ')}
              </Tooltip>
            }
          >
            <Avatar $color="#6c757d" $index={maxDisplay}>
              +{remainingCount}
            </Avatar>
          </OverlayTrigger>
        )}
      </CollaboratorAvatars>
    </CollaboratorContainer>
  );
};

export default CollaboratorList;
